import type { KeyboardActionId } from "~/keyboard/actionCatalog";
import type { KeyEventLike, ParsedBinding } from "~/keyboard/bindings";

import { keyPressMatchesEvent } from "~/keyboard/bindings";

export interface ChordBinding {
	actionIds: KeyboardActionId[];
	sequence: ParsedBinding;
}

export interface ChordControllerOptions {
	chords: ChordBinding[];
	onDispatch: (actionIds: KeyboardActionId[], event: KeyEventLike) => void;
	onPendingChange?: (prefix: string | null) => void;
	timeoutMs?: number;
}

export interface ChordController {
	handleKeydown: (event: KeyEventLike) => boolean;
	reset: () => void;
}

const DEFAULT_CHORD_TIMEOUT_MS = 1500;

const isEscapeEvent = (event: KeyEventLike): boolean => event.key.toLowerCase() === "escape";

const uniqueActionIds = (chords: ChordBinding[]): KeyboardActionId[] => [
	...new Set(chords.flatMap((chord) => chord.actionIds))
];

const createChordController = ({
	chords,
	onDispatch,
	onPendingChange,
	timeoutMs = DEFAULT_CHORD_TIMEOUT_MS
}: ChordControllerOptions): ChordController => {
	let pendingKeys: string[] = [];
	let viable: ChordBinding[] = [];
	let timer: ReturnType<typeof setTimeout> | undefined;

	const clearTimer = () => {
		if (timer) {
			clearTimeout(timer);
			timer = undefined;
		}
	};

	const reset = () => {
		const hadPending = pendingKeys.length > 0;
		clearTimer();
		pendingKeys = [];
		viable = [];
		if (hadPending) onPendingChange?.(null);
	};

	const beginPending = (candidates: ChordBinding[], event: KeyEventLike) => {
		pendingKeys = [event.key.toLowerCase()];
		viable = candidates;
		clearTimer();
		timer = setTimeout(reset, timeoutMs);
		onPendingChange?.(pendingKeys.join(" "));
	};

	const extendPending = (candidates: ChordBinding[], event: KeyEventLike) => {
		pendingKeys = [...pendingKeys, event.key.toLowerCase()];
		viable = candidates;
		clearTimer();
		timer = setTimeout(reset, timeoutMs);
		onPendingChange?.(pendingKeys.join(" "));
	};

	const chordsStartingWith = (event: KeyEventLike): ChordBinding[] =>
		chords.filter((chord) => keyPressMatchesEvent(chord.sequence[0], event));

	const handleKeydown = (event: KeyEventLike): boolean => {
		if (isEscapeEvent(event)) {
			if (pendingKeys.length > 0) {
				reset();
				return true;
			}
			return false;
		}

		const depth = pendingKeys.length;
		const currentViable = depth === 0 ? chords : viable;
		const advanced = currentViable.filter(
			(chord) => depth < chord.sequence.length && keyPressMatchesEvent(chord.sequence[depth], event)
		);

		if (advanced.length === 0) {
			const wasPending = depth > 0;
			reset();
			if (wasPending) {
				const restart = chordsStartingWith(event);
				if (restart.length > 0) {
					beginPending(restart, event);
					return true;
				}
			}
			return false;
		}

		const completed = advanced.filter((chord) => chord.sequence.length === depth + 1);
		if (completed.length > 0) {
			const actionIds = uniqueActionIds(completed);
			reset();
			onDispatch(actionIds, event);
			return true;
		}

		if (depth === 0) beginPending(advanced, event);
		else extendPending(advanced, event);
		return true;
	};

	return { handleKeydown, reset };
};

export default createChordController;
